import { useRef, useState, useCallback } from "react";

/**
 * Hook for inline text editing on double-click.
 * Works with any component that has text props.
 *
 * Usage:
 *   const { editingField, getEditableProps } = useInlineEdit(setProp);
 *
 *   <span {...getEditableProps("text", text, (v) => (props.text = v))}>
 *     {text}
 *   </span>
 */

type SetPropFn = (cb: (props: Record<string, unknown>) => void) => void;

export const useInlineEdit = (setProp: SetPropFn) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const editRef = useRef<HTMLElement>(null);

  const startEditing = useCallback((field: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingField(field);
    setTimeout(() => {
      const el = editRef.current;
      if (el) {
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 0);
  }, []);

  const stopEditing = useCallback((currentValue: string, updater: (value: string) => void) => {
    setEditingField(null);
    const newValue = editRef.current?.innerText || currentValue;
    if (newValue !== currentValue) {
      setProp((props) => updater.call(null, newValue));
    }
  }, [setProp]);

  /**
   * Get props to spread onto an element to make it inline-editable.
   *
   * @param field - unique field identifier (e.g. "title", "navItem-0")
   * @param currentValue - current text value
   * @param updater - function that receives new value and updates the prop
   *                  e.g. (v) => { props.text = v; }
   */
  const getEditableProps = useCallback(
    (
      field: string,
      currentValue: string,
      updater: (value: string) => void,
    ) => {
      const isEditing = editingField === field;
      return {
        ref: isEditing ? editRef : undefined,
        contentEditable: isEditing || undefined,
        suppressContentEditableWarning: true as const,
        onDoubleClick: (e: React.MouseEvent) => startEditing(field, e),
        onBlur: isEditing
          ? () => stopEditing(currentValue, updater)
          : undefined,
        onKeyDown: isEditing
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                (e.target as HTMLElement).blur();
              }
              if (e.key === "Escape") {
                if (editRef.current) editRef.current.innerText = currentValue;
                (e.target as HTMLElement).blur();
              }
            }
          : undefined,
        style: isEditing
          ? { outline: "2px solid #1976d2", outlineOffset: "2px", cursor: "text" as const, minWidth: "20px" }
          : undefined,
      };
    },
    [editingField, startEditing, stopEditing],
  );

  return { editingField, getEditableProps };
};
